import { Button } from "$components/button/button";
import { Callout } from "$components/callout";
import { styled, Typography } from "@material-ui/core";
import { FormattedMessage } from "react-intl";
import { VaccinationNudgeType } from "../../_lib/vaccinations-service/provider";
import type { VaccineCode } from "../../_lib/vaccinations-service/vaccination-schedule";

const HorizontalContainer = styled("div")(() => ({
  display: "flex",
  flexDirection: "row",
  justifyContent: "space-between",
  alignItems: "center",
  width: "100%",
  flex: 1,
}));

const NudgeActionText = styled(Typography)(({ theme }) => ({
  fontWeight: theme.typography.fontWeightBold as any,
  paddingRight: theme.spacing(3),
}));

const concatenateVaccineDisplayNames = (
  vaccines: Array<{ displayName: string }>,
): string => {
  if (!vaccines || !vaccines.length) return "";
  return vaccines.reduce((acc, curr, idx, arr) => {
    if (idx === 0) return curr.displayName;
    if (idx === arr.length - 1) return `${acc} and ${curr.displayName}`;
    return `${acc}, ${curr.displayName}`;
  }, "");
};

export const VaccinationNudge: React.FC<{
  type: VaccinationNudgeType;
  vaccines?: Array<{ displayName: string; code: VaccineCode }>;
  actionCallback?: (vaccines: { displayName: string; code: string }[]) => void;
  actionButtonText?: string;
}> = ({ type, vaccines, actionCallback, actionButtonText }) => {
  const formattedVaccines = concatenateVaccineDisplayNames(vaccines);

  switch (type) {
    case VaccinationNudgeType.MISSING_VACCINES: {
      const formattedVaccines = concatenateVaccineDisplayNames(vaccines);

      return (
        <Callout type="info">
          <HorizontalContainer>
            <Typography>
              <FormattedMessage
                id="vaccination-form-nudge.missing-vaccines"
                defaultMessage="No record of {vaccines}"
                values={{ vaccines: formattedVaccines }}
              />
            </Typography>
            {actionCallback && actionButtonText && (
              <Button
                variant="text"
                color="primary"
                onClick={() => actionCallback(vaccines)}
              >
                <NudgeActionText variant="h5">
                  {actionButtonText}
                </NudgeActionText>
              </Button>
            )}
          </HorizontalContainer>
        </Callout>
      );
    }
    case VaccinationNudgeType.EARLY_VACCINES:
      return (
        <Callout type="info">
          <Typography>
            <FormattedMessage
              id="vaccination-form-nudge.early-vaccines"
              defaultMessage="This patient is not yet due for {vaccines}"
              values={{ vaccines: formattedVaccines }}
            />
          </Typography>
        </Callout>
      );
    case VaccinationNudgeType.DUPLICATE_VACCINES:
      return (
        <Callout type="info">
          <Typography>
            <FormattedMessage
              id="vaccination-form-nudge.duplicate-vaccines"
              defaultMessage="This patient has already had {vaccines}"
              values={{ vaccines: formattedVaccines }}
            />
          </Typography>
        </Callout>
      );
    case VaccinationNudgeType.UNUSUAL_HISTORY:
      return (
        <Callout type="info">
          <Typography>
            <FormattedMessage
              id="vaccination-form-nudge.unusual-history"
              defaultMessage="Unusual history for {vaccines}"
              values={{ vaccines: formattedVaccines }}
            />
          </Typography>
        </Callout>
      );
    default:
      return null;
  }
};
